/* eslint-disable */

import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { supabase } from "./utils/supabaseClient";
import { COLORS } from "./styles/colors";

const todayLocalDate = () => new Date().toLocaleDateString("fr-CA");

const addDays = (dateStr, days) => {
  const date = new Date(`${dateStr}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString("fr-CA");
};

const buildReviewUrl = ({ name, email, courseTypeCode }) => {
  const params = new URLSearchParams({
    store: "sozcode.com",
    user: name,
    order_id: `${courseTypeCode}${email}`,
    email,
    products: courseTypeCode,
    type: "combined",
  });

  return `https://www.reviews.io/store/landing_new_review?${params.toString()}`;
};

export const EvaluationPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    course_type_code: "",
  });
  const [courseOptions, setCourseOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    document.title = "Course Evaluation | Sozcode";

    return () => {
      document.title = "Learn @ Sozcode";
    };
  }, []);

  useEffect(() => {
    const fetchCourseOptions = async () => {
      setLoadingOptions(true);
      setLoadError("");

      const today = todayLocalDate();
      const windowStart = addDays(today, -14);
      const windowEnd = addDays(today, 7);

      const [courseTypesResult, courseRunsResult] = await Promise.all([
        supabase.from("course_type").select("code, name").order("name"),
        supabase
          .from("course_run")
          .select("course_type_code, start_date, end_date")
          .lte("start_date", windowEnd)
          .gte("end_date", windowStart)
          .order("start_date", { ascending: true }),
      ]);

      if (courseTypesResult.error) {
        console.error("Error fetching course types:", courseTypesResult.error);
        setLoadError("We could not load the course list. Please refresh and try again.");
        setLoadingOptions(false);
        return;
      }

      if (courseRunsResult.error) {
        console.error("Error fetching course runs:", courseRunsResult.error);
      }

      const prioritizedCodes = [
        ...new Set((courseRunsResult.data || []).map((run) => run.course_type_code)),
      ];
      const prioritizedSet = new Set(prioritizedCodes);
      const courseTypes = courseTypesResult.data || [];

      const byCode = new Map(courseTypes.map((course) => [course.code, course]));
      const prioritized = prioritizedCodes
        .map((code) => byCode.get(code))
        .filter(Boolean);
      const remaining = courseTypes
        .filter((course) => !prioritizedSet.has(course.code))
        .sort((a, b) => a.name.localeCompare(b.name));

      setCourseOptions([...prioritized, ...remaining]);
      setLoadingOptions(false);
    };

    fetchCourseOptions();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (submitEvent) => {
    submitEvent.preventDefault();
    setSubmitError("");

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const courseTypeCode = form.course_type_code;

    if (!name || !email || !courseTypeCode) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    window.location.assign(buildReviewUrl({ name, email, courseTypeCode }));
  };

  return (
    <Container maxW="640px" py={{ base: 8, lg: 12 }} px={{ base: 4, md: 6 }}>
      <Box bg="white" borderRadius="lg" boxShadow="md" p={{ base: 6, lg: 8 }}>
        <Stack spacing={5} as="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Heading size="md" color={COLORS.maastrichtBlue}>
              Sozcode Course Evaluation
            </Heading>
            <Text color={COLORS.shadowBlue}>
              After clicking submit, you will be redirected to another page to fill in your evaluation.
            </Text>
          </Stack>

          {loadError && (
            <Alert status="error">
              <AlertIcon />
              {loadError}
            </Alert>
          )}

          {submitError && (
            <Alert status="error">
              <AlertIcon />
              {submitError}
            </Alert>
          )}

          <FormControl isRequired>
            <FormLabel color={COLORS.shadowBlue} fontWeight="600">
              Name
            </FormLabel>
            <Input
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Your full name"
              bg="white"
              borderColor={COLORS.shadowBlue}
              _hover={{ borderColor: COLORS.maastrichtBlue }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel color={COLORS.shadowBlue} fontWeight="600">
              Email (Please leave your personal email)
            </FormLabel>
            <Input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="example@example.com"
              bg="white"
              borderColor={COLORS.shadowBlue}
              _hover={{ borderColor: COLORS.maastrichtBlue }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel color={COLORS.shadowBlue} fontWeight="600">
              Select the course type
            </FormLabel>
            {loadingOptions ? (
              <Spinner size="sm" color={COLORS.mandarin} />
            ) : (
              <Select
                value={form.course_type_code}
                onChange={handleChange("course_type_code")}
                placeholder="Please Select"
                bg="white"
                borderColor={COLORS.shadowBlue}
                _hover={{ borderColor: COLORS.maastrichtBlue }}
              >
                {courseOptions.map((course) => (
                  <option key={course.code} value={course.code}>
                    {course.name}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>

          <Button
            type="submit"
            isDisabled={loadingOptions || !!loadError}
            bg={COLORS.mandarin}
            color="white"
            _hover={{ bg: "#e06835" }}
          >
            Submit
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};
